import { SITE_PHOTOS } from '../sitePhotos';
import ConstructionPhotos from '../components/ConstructionPhotos';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { Calendar, ChevronRight, Loader2, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import AdBlock from '../components/AdBlock';
import { useSiteInfo } from '../context/SiteContext';
import { useState, useEffect } from 'react';
import { publicCollection } from '../lib/publicContent';
import { ogImageForMeta } from '../lib/ogImageUrl';
import { optimizeDataImages, cleanQuillHtml } from '../lib/imageCompression';
import CommentSection from '../components/CommentSection';
import FacebookComments from '../components/FacebookComments';
import { ArticleSchema } from '../components/SEO';

interface Article {
  id: string | number;
  title: string;
  slug: string;
  image: string;
  date: string;
  excerpt: string;
  content: string;
  publishAt?: string;
}

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const { siteInfo, latestPosts } = useSiteInfo();
  
  useEffect(() => {
    let cancelled = false;
    const fetchArticle = async () => {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 220));
      try {
        const [posts] = await Promise.all([publicCollection('posts'),minDelay]);
        if (cancelled) return;
        setArticle(optimizeDataImages(posts.find(p=>p.slug===slug) || null) as Article|null);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchArticle();
    return () => { cancelled = true; };
  }, [slug]);

  const relatedArticles = latestPosts.filter(n => n.slug !== slug).slice(0, 3);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : (siteInfo.baseUrl || "https://khoancocnhoivietnam.vn");
  const canonicalUrl = article ? `${baseUrl}/tin-tuc/${article.slug}` : `${baseUrl}/tin-tuc`;
  const defaultOgImage =
    "https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/Anh_khoan_coc_nhoi/z6586686864376_5d6f4365575fca0b349b19cf7fd3940c.jpg";
  const ogImageUrl = ogImageForMeta(article?.image || '', defaultOgImage);
  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareTitle = encodeURIComponent(article?.title || '');

  if (!loading && !article) return <main className="max-w-3xl mx-auto px-5 py-16">
    <h1 className="text-2xl font-bold text-primary">Bài viết tạm thời chưa tải được</h1>
    <p className="mt-4">Nội dung đang được khôi phục. Anh/chị có thể xem các bài khác hoặc liên hệ để được tư vấn.</p>
    <div className="flex flex-wrap gap-5 mt-6"><Link to="/tin-tuc" className="underline">Xem các bài viết</Link><a href={`tel:${siteInfo.phone}`} className="underline">Gọi tư vấn: {siteInfo.phone}</a></div>
  </main>;

  const breadcrumbSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Trang chủ", "item": "https://www.khoancocnhoivietnam.vn" },
      { "@type": "ListItem", "position": 2, "name": "Tin tức", "item": "https://www.khoancocnhoivietnam.vn/tin-tuc" },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": canonicalUrl }
    ]
  } : null;

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      {article && (
        <Helmet>
          <title>{article.title} - Nền Móng Việt Nam</title>
          <meta name="robots" content={article.id === 0 ? "noindex, follow" : "index, follow"} />
          <meta name="description" content={article.excerpt} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.excerpt} />
          <meta property="og:image" content={ogImageUrl} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="Nền Móng Việt Nam" />
          <meta property="og:locale" content="vi_VN" />
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      )}

      {article && (
        <ArticleSchema
          headline={article.title}
          description={article.excerpt}
          image={ogImageUrl}
          url={canonicalUrl}
          datePublished={article.publishAt || article.date}
          authorType="Organization"
          author="Nền Móng Việt Nam"
          publisher="Nền Móng Việt Nam"
          publisherLogo={siteInfo.logoUrl}
        />
      )}

      <div className="w-full md:w-3/4 min-w-0">
        {loading || !article ? (
          <div className="bg-white rounded-lg shadow-sm border min-h-[60vh] flex flex-col items-center justify-center p-12">
            <Loader2 className="animate-spin text-primary mb-3" size={36} />
            <span className="text-sm text-gray-400 font-medium">Đang tải bài viết...</span>
          </div>
        ) : (
          <motion.div
            key={article.slug}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link to="/" className="hover:text-primary whitespace-nowrap">Trang chủ</Link>
              <span>/</span>
              <Link to="/tin-tuc" className="hover:text-primary whitespace-nowrap">Tin tức</Link>
              <span>/</span>
              <span className="text-gray-800 font-medium truncate break-words max-w-full">{article.title}</span>
            </nav>

            <article className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-sm border">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-4 uppercase leading-snug">
                {article.title}
              </h1>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-500 mb-6 pb-6 border-b gap-4">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  <span>Đăng ngày: {article.date}</span>
                </div>
                
                <div className="flex items-center space-x-3 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
                  <span className="font-medium text-gray-700 hidden sm:inline-block">Chia sẻ:</span>
                  <button 
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, '_blank')}
                    className="p-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                    title="Chia sẻ lên Facebook"
                  >
                    <Facebook size={16} />
                  </button>
                  <button 
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`, '_blank')}
                    className="p-2 bg-sky-50 text-sky-500 rounded-full hover:bg-sky-100 transition-colors"
                    title="Chia sẻ lên Twitter"
                  >
                    <Twitter size={16} />
                  </button>
                  <button 
                    onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`, '_blank')}
                    className="p-2 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                    title="Chia sẻ lên LinkedIn"
                  >
                    <Linkedin size={16} />
                  </button>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(canonicalUrl);
                      alert('Đã sao chép đường dẫn!');
                    }}
                    className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                    title="Sao chép đường dẫn"
                  >
                    <LinkIcon size={16} />
                  </button>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src={article.image && !/placehold\.co|dummyimage\.com/.test(article.image) ? article.image : SITE_PHOTOS[0].url} 
                  alt={article.title} 
                  className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = SITE_PHOTOS[0].url;
                  }}
                />
              </div>

              <div className="prose max-w-none text-gray-700 leading-relaxed break-words">
                {article.excerpt && <p className="font-bold text-lg mb-6">{article.excerpt}</p>}
                
                {article.content ? (
                  <div 
                    className="article-content ql-editor break-words"
                    dangerouslySetInnerHTML={{ __html: cleanQuillHtml(article.content) }} 
                  />
                ) : (
                  <div className="italic text-center text-gray-500 py-8 bg-gray-50 rounded border border-dashed">
                    [Nội dung chi tiết của bài viết "{article.title}" đang chờ cập nhật từ dữ liệu mẫu của bạn.]
                  </div>
                )}
              </div>
              
              {!/site-photos\/|Anh_khoan_coc_nhoi\//i.test(article.content || "") && <ConstructionPhotos />}
              <AdBlock position="in_article" className="mt-8 pt-8 border-t" />
              
              {/* Comment Section */}
              {siteInfo.enableComments && (siteInfo.commentType === 'firebase' || siteInfo.commentType === 'both') && (
                <CommentSection 
                  postId={article.slug} 
                  postType="news" 
                  enabled={true}
                />
              )}
              {siteInfo.enableComments && (siteInfo.commentType === 'facebook' || siteInfo.commentType === 'both') && (
                <FacebookComments 
                  url={`${siteInfo.baseUrl}/tin-tuc/${article.slug}`}
                  enabled={true}
                  facebookCommentCode={siteInfo.facebookCommentCode}
                />
              )}
            </article>

            {relatedArticles.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-primary uppercase mb-6 pb-2 border-b-2 border-accent inline-block">
                  Bài viết liên quan
                </h2>
                <div className="mobile-card-rail grid grid-cols-1 md:grid-cols-3 gap-6" tabIndex={0} role="region" aria-label="Bài viết liên quan">
                  {relatedArticles.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg overflow-hidden shadow-sm border hover:shadow-md transition-shadow group">
                      <Link to={`/tin-tuc/${item.slug}`} className="block overflow-hidden aspect-video">
                        <img 
                          src={item.image} 
                          alt={item.title} 
                            className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/600x400/eeeeee/999999?text=News+Image';
                          }}
                        />
                      </Link>
                      <div className="p-4">
                        <h3 className="font-bold text-sm mb-2 line-clamp-2 uppercase">
                          <Link to={`/tin-tuc/${item.slug}`} className="hover:text-primary transition-colors">
                            {item.title}
                          </Link>
                        </h3>
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar size={12} className="mr-1" />
                          <span>{item.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}

